import { action, Action } from 'easy-peasy';
import mixpanel from 'utils/mixpanel-frontend';
import { useStoreActions } from 'hooks';
import JWT from 'jwt-decode';
import { getFlow } from '@cognite/auth-utils';
import { PublicClientApplication } from '@azure/msal-browser';
import { ClientInfo, Cluster } from 'components/TenantSelector/types';
import * as Sentry from '@sentry/react';
import { isString } from 'utils/misc';
import { useEnv } from '../hooks/env';

export enum LoginType {
  apikey,
  oauth,
  tenant,
}

/**
 * This is from https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/Overview/appId/86057cba-4df2-4613-a27d-e747e17a5305/isMSAApp/. The configs are in Terraform.
 */
const AZURE_APP_ID = '86057cba-4df2-4613-a27d-e747e17a5305';

export const getClientId = (clientId: string | undefined) =>
  clientId || AZURE_APP_ID;

export const publicClient = new PublicClientApplication({
  auth: {
    clientId: AZURE_APP_ID,
  },
  cache: { cacheLocation: 'localStorage' },
});

export interface AuthModel {
  apiKey: string | undefined;
  tenant: string | undefined;
  token: string | undefined;
  expiry: number | undefined;
  cluster: string | undefined;
  email: string | undefined;
  loggedIn: boolean | undefined;
  // methods
  setLoggedIn: Action<
    AuthModel,
    | {
        apiKey?: string;
        tenant?: string;
        email?: string;
        cluster?: string;
        loginType: LoginType;
      }
    | false
  >;
  updateToken: Action<AuthModel, string | undefined>;
  updateTenant: Action<AuthModel, string | undefined>;
}

export const API_KEY_LOCALSTORAGE_KEY = '__itg__api_key';
export const TENANT_LOCALSTORAGE_KEY = '__itg__tenant';
export const CLUSTER_LOCALSTORAGE_KEY = '__itg__cluster';
export const ACCOUNT_LOCALSTORAGE_KEY = 'accountLocalId';
const MACHINE_TOKEN = '__itg__machine_token';

export const authModel: AuthModel = {
  apiKey: localStorage.getItem(API_KEY_LOCALSTORAGE_KEY) || undefined,
  tenant: localStorage.getItem(TENANT_LOCALSTORAGE_KEY) || undefined,
  cluster: undefined,
  email: undefined,
  token: undefined,
  expiry: undefined,
  loggedIn: undefined,
  // methods
  setLoggedIn: action((state, params) => {
    if (!params) {
      state.apiKey = undefined;
      state.token = undefined;
      state.tenant = undefined;
      state.cluster = undefined;
      state.email = undefined;
      state.loggedIn = undefined;
    } else {
      const { apiKey, tenant, cluster, loginType, email } = params;
      if (tenant && tenant.length !== 0) {
        localStorage.setItem(TENANT_LOCALSTORAGE_KEY, tenant);
        state.tenant = tenant;
      }
      if (apiKey && apiKey.length !== 0) {
        localStorage.setItem(API_KEY_LOCALSTORAGE_KEY, apiKey);
        state.apiKey = apiKey;
        state.token = apiKey;
      }
      if (cluster && cluster.length !== 0) {
        localStorage.setItem(CLUSTER_LOCALSTORAGE_KEY, cluster);
        state.cluster = cluster;
      }
      if (email && email.length !== 0) {
        state.email = email.toLowerCase();
      }
      if (loginType.toString()) {
        state.loggedIn = true;
      }
    }
  }),
  updateToken: action((state, token) => {
    state.token = token;
    let expiry = Math.max();
    try {
      expiry = JSON.parse(atob(token?.split('.')[1] || '')).exp;
      state.expiry = expiry;
    } catch (error) {
      Sentry.captureMessage('Unable to extract token expiry');
    }
    if (!state.tenant)
      // solving temp gql 500 error for requests with token but without project in header
      state.tenant = localStorage.getItem(TENANT_LOCALSTORAGE_KEY) || undefined;
  }),
  updateTenant: action((state, tenant) => {
    state.tenant = tenant;
  }),
};

const getCluster = (env: string | Cluster) =>
  isString(env) ? env : (env as Cluster).cluster;

export const generateLoginOptions = (
  client: ClientInfo,
  options: any
): any => ({
  auth: {
    clientId: client.id,
    authority: `https://login.microsoftonline.com/${options?.directory}`,
  },
  caches: { cacheLocation: 'localStorage' },
});

export const useLogin = () => {
  const setLoggedIn = useStoreActions((state) => state.auth.setLoggedIn);
  const env = useEnv();
  const cluster = getCluster(env);
  const postOAuthLogin = usePostOAuthLogin();
  return async ({
    apiKey,
    tenant,
    useOIDC,
    forceRefresh = false,
  }: {
    apiKey?: string;
    tenant?: string;
    useOIDC?: boolean;
    forceRefresh?: boolean;
  }) => {
    const { flow, options } = getFlow(tenant, cluster);
    if (useOIDC) {
      if (flow && options && env && tenant) {
        const accountId = localStorage.getItem(ACCOUNT_LOCALSTORAGE_KEY) || '';
        if (!accountId) return { loggedIn: false }; // throw new Error('no user_id found');
        // Get account by ID.
        const account = publicClient.getAccountByLocalId(accountId);
        if (!account) throw new Error('no user found');

        const machineToken = JSON.parse(
          localStorage.getItem(MACHINE_TOKEN) || '""'
        );
        let cdfToken: string | null = '';
        if (!machineToken) {
          cdfToken = (
            await publicClient.acquireTokenSilent({
              account,
              authority: `https://login.microsoftonline.com/${options?.directory}`,
              scopes: [
                `https://${
                  (env as Cluster).cluster || 'api'
                }.cognitedata.com/IDENTITY`,
                `openid`,
                'offline_access',
                'profile',
              ],
              forceRefresh,
            })
          ).accessToken;
        }

        if (cdfToken || machineToken) {
          const user = account.username || 'empty';
          postOAuthLogin(tenant, cdfToken || machineToken);

          mixpanel.identify(account.localAccountId);
          mixpanel.people.set({
            $name: account.name,
            cdfTenant: tenant, // store tenant as a profile property, not as the identity
          });
          mixpanel.people.set_once({
            first_sign_up_date: new Date().toISOString(),
          });
          mixpanel.register({
            cdfTenant: tenant,
            user_id: account.localAccountId, // also register user ID as super property for easier querying
          });
          mixpanel.track('Logged in');

          setLoggedIn({
            tenant,
            email: user,
            apiKey,
            loginType: LoginType.tenant,
          });
          return {
            loggedIn: true,
            tenant,
            user,
            token: cdfToken || machineToken,
          };
        }
      }
      return { loggedIn: false };
    }
    setLoggedIn(false);
    return { loggedIn: false };
  };
};
export const usePostOAuthLogin = () => {
  const setLoggedIn = useStoreActions((state) => state.auth.setLoggedIn);
  const updateToken = useStoreActions((state) => state.auth.updateToken);
  const env = useEnv();
  const cluster = getCluster(env);
  return async (tenant?: string, token?: string) => {
    if (token) {
      if (tenant) localStorage.setItem(TENANT_LOCALSTORAGE_KEY, tenant);
      updateToken(token);
      // unique name is usually supplied via AAD token to indicate the user's unique identifier if email is missing
      // eslint-disable-next-line camelcase

      const response = JWT<Record<string, string>>(token);
      const email =
        response.email ||
        response.unique_name ||
        response['https://twindata.io/email'] ||
        'machineCredential';
      setLoggedIn({
        tenant,
        email,
        loginType: LoginType.oauth,
        cluster: cluster || 'api',
      });
      mixpanel.people.set_once({
        cdfTenant: tenant,
      });
      mixpanel.identify(tenant);
      mixpanel.register({
        cdfTenant: tenant,
      });
    }
  };
};
